/**
 * Sentry Error Tracking — Initialization & Helpers
 *
 * Provides centralized Sentry setup and typed helper functions
 * for error capture, user context, and breadcrumbs.
 * Gracefully degrades when @sentry/nextjs is not installed or SENTRY_DSN is not configured.
 *
 * NOTE: The canonical Sentry.init() calls live in sentry.client.config.ts,
 * sentry.server.config.ts, and sentry.edge.config.ts — those files are loaded
 * automatically by @sentry/nextjs. This module exists solely to expose typed
 * helper wrappers (captureError, setUser, etc.) that the rest of the app imports.
 */

import { createLogger } from "./logger";

const logger = createLogger("sentry");

// NEXT_PUBLIC_SENTRY_DSN is the canonical env var (public prefix so it is
// available in browser bundles). SENTRY_DSN is accepted as a server-side
// fallback for legacy deployments.
const SENTRY_DSN =
  process.env.NEXT_PUBLIC_SENTRY_DSN || process.env.SENTRY_DSN;

let Sentry: typeof import("@sentry/nextjs") | null = null;

function getSentry(): typeof import("@sentry/nextjs") | null {
  return Sentry;
}

/**
 * Synchronously import @sentry/nextjs and cache the module reference.
 * Called once at module load time so helper functions are immediately usable.
 */
function loadSentrySync() {
  if (!SENTRY_DSN) return;
  try {
    // @sentry/nextjs is a hard dependency — the dynamic import below resolves
    // synchronously in Node/Edge because the module is already in the bundle.
    // We keep the try/catch in case someone runs without the package installed.
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    Sentry = require("@sentry/nextjs") as typeof import("@sentry/nextjs");
  } catch {
    // Package not available — all helpers will no-op gracefully.
  }
}

loadSentrySync();

/**
 * initSentry is kept for backwards-compatibility. The real Sentry.init() is
 * now called directly by the three sentry.*.config.ts entry-point files that
 * @sentry/nextjs loads automatically. Calling this function again would be a
 * no-op (Sentry guards against double-init), but we keep the export so
 * existing call-sites don't break.
 */
export function initSentry() {
  if (!SENTRY_DSN) {
    logger.warn("NEXT_PUBLIC_SENTRY_DSN not set — Sentry disabled");
  }
  // Actual init is handled by sentry.client/server/edge.config.ts
}

export function captureError(error: Error | unknown, context?: Record<string, unknown>) {
  const sentry = getSentry();
  if (!sentry) {
    logger.error("Sentry not initialized — error not captured", {}, error instanceof Error ? error : undefined);
    return;
  }

  if (context) {
    sentry.withScope((scope) => {
      Object.entries(context).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
      sentry.captureException(error);
    });
  } else {
    sentry.captureException(error);
  }
}

export function setUser(user: { id: string; email?: string; role?: string }) {
  getSentry()?.setUser(user);
}

export function clearUser() {
  getSentry()?.setUser(null);
}

export function addBreadcrumb(message: string, category?: string, data?: Record<string, unknown>) {
  getSentry()?.addBreadcrumb({ message, category, data, level: "info" });
}
